import { Form } from 'module/ui/components/atoms/form';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Radio } from 'module/ui/components/atoms/radio';
import { faTrash, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { Select } from '@rocketlane/ui';
import { Skeleton } from 'antd';
import { SignalOption } from 'api/custom-section/types';
import { useGetAllSignalConfigurationsQuery } from 'queries/signal';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SectionContent } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { signalTypeConfigs } from 'components/organisms/signal/signal-type/type-configs';
import { DoneButton } from '../../../shared/section-styles';
import { ThrashButton } from '../../../shared/section-styles';
import type { Option } from '@rocketlane/ui';

const ContentBlock = styled.div`
  /* padding: 0 16px; */
`;

const RadioGroup = styled(Radio.Group)`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .ant-radio-wrapper {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-right: 0;
  }

  .radio-label {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-top: 1px;
  }

  .label-text {
    font-size: 14px;
    line-height: 1.286em;
    color: #161616;
  }

  .help-text {
    font-size: 12px;
    line-height: 1.333em;
    color: #525252;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  gap: 16px;
`;

export const SignalPromptInput = ({
  prompt,
  setPrompt,
  onGenerate,
  isGenerating,
  onCancel,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showCancelButton = false,
}) => {
  const [form] = Form.useForm();

  const handleSubmit = (values) => {
    onGenerate({
      signalType: values.signalType,
      signalsSelected: values.signalsSelected,
    });
  };

  const { data: allSignals, isLoading: isSignalsLoading } =
    useGetAllSignalConfigurationsQuery();

  if (isSignalsLoading) {
    return <Skeleton active />;
  }

  const signalOptions = allSignals?.map((signal) => ({
    label: signal.signalName,
    value: signal.signalConfigurationId,
    color: signalTypeConfigs[signal.signalType].color,
  }));

  return (
    <SectionContent>
      <ContentBlock>
        <Form
          form={form}
          initialValues={{
            signalType: prompt?.signalType || SignalOption.ALL,
            signalsSelected: prompt?.signalsSelected || [],
          }}
          onFinish={handleSubmit}
          layout="vertical"
        >
          <Form.Item name="signalType" shouldUpdate>
            <RadioGroup
              defaultValue={prompt?.signalType || SignalOption.ALL}
              onChange={(e) => {
                if (e.target.value === SignalOption.ALL) {
                  setPrompt({
                    signalType: e.target.value,
                    signalsSelected: [],
                  });
                  form.setFieldsValue({
                    signalsSelected: [],
                  });
                } else {
                  setPrompt({
                    ...prompt,
                    signalType: e.target.value,
                  });
                }
              }}
            >
              <Radio value={SignalOption.ALL}>
                <div className="radio-label">
                  <span className="label-text">All Signals</span>
                  <span className="help-text">Identified in the meeting</span>
                </div>
              </Radio>
              <Radio value={SignalOption.SPECIFIC}>
                <span className="label-text">Specific...</span>
              </Radio>
            </RadioGroup>
          </Form.Item>

          <Form.Item
            name="signalsSelected"
            label={<FormattedMessage id="signals" />}
            hidden={prompt?.signalType !== SignalOption.SPECIFIC}
          >
            <Select
              isMulti={true}
              value={prompt?.signalsSelected || ([] as Option[])}
              variant="tag"
              options={signalOptions as Option[]}
              closeMenuOnSelect={false}
              onChange={(value) => {
                setPrompt({ ...prompt, signalsSelected: value as any });
              }}
            />
          </Form.Item>

          <ButtonGroup>
            <ActionButtons>
              <ThrashButton variant="secondary" onClick={onCancel}>
                <FontAwesomeIcon icon={faTrash} />
              </ThrashButton>
              <DoneButton
                variant="primary"
                onClick={() => form.submit()}
                disabled={
                  isGenerating ||
                  isSignalsLoading ||
                  !prompt?.signalType ||
                  (prompt?.signalType === SignalOption.SPECIFIC &&
                    !prompt?.signalsSelected.length)
                }
              >
                <FontAwesomeIcon icon={faCheck} />
                <FormattedMessage id="rich_text_editor.sections.done" />
              </DoneButton>
            </ActionButtons>
          </ButtonGroup>
        </Form>
      </ContentBlock>
    </SectionContent>
  );
};
